import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { CheckCircle, X, RotateCcw, Plus, Trash2, AlertTriangle, ShieldCheck } from 'lucide-react';

/**
 * D17 — canonical Solution Bank FULL REVIEW surface.
 *
 * This replaces the previous one-click Approve. A Manager/Admin must open the complete candidate,
 * author ordered steps + outcome + category, confirm sanitization, and make a duplicate decision
 * before Approve is enabled. The BACKEND re-validates every rule (this is defense-in-depth, never the
 * only gate). Rendered as a viewport-bounded overlay (D12): max-h-[90vh] + internal scroll, footer
 * actions always reachable, no raw IDs surfaced.
 */
const PLACEHOLDER_STEPS = new Set([
  '', 'test', 'test script', 'test scripts', 'done', 'fixed', 'resolved', 'restarted',
  'human agent helped', 'issue closed', 'closed', 'ok', 'okay', 'n/a', 'na', 'none', '-',
]);

const DUP_DECISIONS = ['new', 'duplicate', 'improvement', 'merge'];

function scanForSensitive(text) {
  const s = String(text || '');
  const out = [];
  if (/[\w.+-]+@[\w-]+\.[\w.-]+/.test(s)) out.push('a possible email address');
  if (/\b\d{7,}\b/.test(s)) out.push('a long numeric sequence (possible phone or ID)');
  if (/(sk_[a-z0-9]|api[_-]?key|password|secret|token|bearer\s)/i.test(s)) out.push('a possible credential or secret');
  if (/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-/i.test(s)) out.push('a raw system identifier (UUID)');
  return out;
}

// Client mirror of the backend deterministic resolution-QUALITY gate (solutions_routes.py
// resolution_quality_class / _RES_QUALITY_MSG). DEFENSE-IN-DEPTH only — the backend re-validates on
// /approve and remains authoritative; this makes an invalid candidate obviously non-approvable in the UI
// instead of surfacing a late 422. Rejects non-RESOLUTION content (a callback time, follow-up promise,
// handoff, escalation or bare status word) that is non-empty but is not a reusable corrective action.
// Any concrete corrective ACTION always passes, so real multi-step fixes never false-disable.
const RES_ACTION_RE = /\b(restart|reboot|reset|reconnect|reinstall|install|replace|clear|remov(e|ed)|delet(e|ed)|updat(e|ed)|upgrad(e|ed)|configur(e|ed)|enabl(e|ed)|disabl(e|ed)|unplug|re-?plug|reseat|check|verif(y|ied)|test(ed)?|run|ran|set|adjust|clean(ed)?|flush(ed)?|drain(ed)?|refill|tighten|swap(ped)?|toggl(e|ed)|apply|applied|power[- ]?(off|on|cycle)|press(ed)?( and hold)?|hold down|navigat(e|ed)|select(ed)?|enter(ed)?|disconnect(ed)?)\b/i;
const RES_NONRES = [
  ['SCHEDULING_ONLY', /\b(call (you|them|back|again)|call ?back|by \d{1,2}\s*[:.]?\d{0,2}\s*(a\.?m|p\.?m)|at \d{1,2}\s*(a\.?m|p\.?m)|tomorrow|later today|this (afternoon|morning|evening)|next week|schedule a call|book a call)\b/i],
  ['FOLLOWUP_ONLY', /\b(follow(ed)?[ -]?up|will follow|reach out|get back to (you|them)|touch base|circle back|check in with)\b/i],
  ['HANDOFF_ONLY', /\b(transfer(red)?|connect(ing)? (you|them)|speak (to|with)|another agent|human( agent)?|live agent|representative|specialist)\b/i],
  ['ESCALATION_ONLY', /\b(escalat(e|ed|ing|ion)|raised to|forwarded to (a )?(manager|supervisor|team)|manager will|requested (a )?(human|manager|supervisor|agent))\b/i],
  ['STATUS_ONLY', /^\s*(resolved|done|fixed|completed|closed|pending|waiting|ok(ay)?|n\/?a|none|sorted|handled|noted|understood)[\s.!]*$/i],
];
const RES_QUESTION = /^\s*(can|could|would|will|shall|may|do|does|is|are|should|when|what time)\b/i;
export const RES_QUALITY_MSG = "Resolution Steps must describe what actually resolved the customer's issue. Callbacks, follow-up plans, escalation/handoff notes, or status words are not reusable solutions.";

// Returns "" for an acceptable reusable resolution, else a reason class
// (EMPTY/SCHEDULING_ONLY/FOLLOWUP_ONLY/HANDOFF_ONLY/ESCALATION_ONLY/STATUS_ONLY/NON_RESOLUTION/INSUFFICIENT).
// Provenance is stored as a machine value and shown as words. Reuses the AI Chat / AI Voice labels
// the review list already displays, so one vocabulary reads the same everywhere.
const CHANNEL_LABELS = {
  ai_chat: 'sb.review.aiChat',
  ai_voice: 'sb.review.aiVoice',
  human_chat: 'sb.review.ch.humanChat',
  voice_call: 'sb.review.ch.voiceCall',
  human_call: 'sb.review.ch.humanCall',
  solution_bank: 'sb.review.ch.solutionBank',
};

// Provenance is a machine value shown as words, and is READ-ONLY here. A reviewer reviews content; the
// record's true origin (e.g. a Knowledge Builder draft is "ai_suggested") must never be reclassified to
// human_resolution/owner_supplied/imported to pass a gate — the owner standard forbids falsifying it.
const SOURCE_LABELS = {
  ai_suggested: 'AI-suggested (Knowledge Builder)',
  ai_generated: 'AI-generated (from a conversation)',
  human_resolution: 'Human resolution',
  conversation_gap: 'Conversation gap',
  owner_supplied: 'Owner supplied',
  imported: 'Imported',
  operator_resolution: 'Operator resolution',
};

const BOOKKEEPING_OUTCOMES = new Set([
  'sanitized', 'updated', 'approved', 'reviewed', 'edited', 'corrected', 'checked', 'verified',
  'done', 'fixed', 'resolved', 'completed', 'closed', 'confirmed', 'ok', 'okay', 'n/a', 'na',
  'none', 'nil', '-', 'test', 'no change', 'not applicable',
]);

export function resolutionQualityClass(resolution) {
  const text = String(resolution || '').trim();
  if (!text) return 'EMPTY';
  if (RES_ACTION_RE.test(text)) return '';           // concrete corrective action → reusable
  for (const [cls, pat] of RES_NONRES) { if (pat.test(text)) return cls; }
  if (RES_QUESTION.test(text)) return 'NON_RESOLUTION';  // a question / request is not a resolution
  if (text.split(/\s+/).length < 4) return 'INSUFFICIENT';
  return '';
}

const shortRef = (id) => (id ? String(id).slice(-6).toUpperCase() : '');

export default function SolutionReviewModal({ solution, businessId, makeAuthenticatedRequest, onClose, onDone, toast, t }) {
  const tr = t || ((k, o) => (o && o.name) || k);

  // Lifecycle-aware mode. A freshly generated Knowledge Builder draft is "suggested": it can be AUTHORED
  // (edit steps + outcome, Save draft, Submit for Review) but NEVER approved from here — the backend rejects
  // approve on a suggested record. Only a candidate already in the review queue (pending_approval /
  // pending_review) exposes the independent-reviewer controls (Approve / Return / Reject + sanitization).
  const curStatus = String(solution.status || '').toLowerCase();
  const isAuthoring = curStatus === 'suggested';
  const isReviewing = curStatus === 'pending_approval' || curStatus === 'pending_review';

  const initialSteps = useMemo(() => {
    if (Array.isArray(solution.resolution_steps) && solution.resolution_steps.length) return solution.resolution_steps;
    const raw = (solution.resolution || '').trim();
    return raw ? raw.split(/\n+/).map((s) => s.trim()).filter(Boolean) : [''];
  }, [solution]);

  const [title, setTitle] = useState(solution.title || '');
  const [problemSummary, setProblemSummary] = useState(solution.problem_summary || solution.description || '');
  const [steps, setSteps] = useState(initialSteps.length ? initialSteps : ['']);
  const [outcome, setOutcome] = useState(solution.outcome || '');
  const [category, setCategory] = useState(solution.category && solution.category !== 'all' ? solution.category : '');
  const [tagsText, setTagsText] = useState((solution.tags || []).join(', '));
  const [source, setSource] = useState(solution.source || 'human_resolution');
  const [sanitizedConfirmed, setSanitizedConfirmed] = useState(false);
  const [duplicateDecision, setDuplicateDecision] = useState(solution.duplicate_decision || 'new');
  const [duplicateOf, setDuplicateOf] = useState(solution.duplicate_of || '');
  const [approvedList, setApprovedList] = useState([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // Tenant CANONICAL categories (Category Management authority). The reviewer may ONLY pick one of these —
  // no free text — so Approve can never send a category the backend gate rejects (e.g. "Moving Service").
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  // Load the tenant's approved solutions so the reviewer can pick a duplicate/supersede target (titles only).
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!businessId) return;
      try {
        const r = await makeAuthenticatedRequest(`/api/v1/solutions/${businessId}?status=approved`);
        if (!r.ok) return;
        const d = await r.json();
        if (alive) setApprovedList((d.solutions || []).filter((s) => s.id !== solution.id));
      } catch { /* non-fatal — reviewer can still choose "new" */ }
    })();
    return () => { alive = false; };
  }, [businessId, makeAuthenticatedRequest, solution.id]);

  // Load the tenant's canonical feedback categories from the SAME Category Management authority the Business
  // Portal uses (GET /api/v1/business/{id}/feedback-categories -> TaxonomyService). The approval gate
  // (_category_in_effective) validates the category NAME against this exact effective set, so the SELECT sends
  // a name that will pass. We never create categories here — a missing category is added in Category Management.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!businessId) return;
      try {
        const r = await makeAuthenticatedRequest(`/api/v1/business/${businessId}/feedback-categories`);
        if (!r.ok) { if (alive) setCategoriesLoaded(true); return; }
        const d = await r.json();
        const raw = Array.isArray(d) ? d
          : Array.isArray(d.categories) ? d.categories
          : Array.isArray(d.feedback_categories) ? d.feedback_categories
          : [];
        // Active only (mirrors Category Management's enabled filter + the effective-category gate).
        const names = raw
          .filter((c) => c && c.is_enabled !== false && c.name)
          .map((c) => String(c.name));
        const unique = Array.from(new Set(names));
        if (alive) { setCategoryOptions(unique); setCategoriesLoaded(true); }
      } catch { if (alive) setCategoriesLoaded(true); }
    })();
    return () => { alive = false; };
  }, [businessId, makeAuthenticatedRequest]);

  // A pre-filled category (e.g. a legacy candidate's "Moving Service") that is NOT one of the tenant's
  // canonical categories must NOT stay selected — force the reviewer to pick a valid one.
  useEffect(() => {
    if (!categoriesLoaded) return;
    if (category && !categoryOptions.includes(category)) setCategory('');
  }, [categoriesLoaded, categoryOptions, category]);

  const meaningfulSteps = steps.map((s) => s.trim()).filter((s) => s && !PLACEHOLDER_STEPS.has(s.toLowerCase()));
  const sanitizeFindings = useMemo(
    () => Array.from(new Set([problemSummary, outcome, ...steps].flatMap(scanForSensitive))),
    [problemSummary, outcome, steps],
  );
  // Resolution-QUALITY reason for THIS candidate (the text the backend will judge on /approve is the
  // joined meaningful steps). Non-empty → the candidate is not a reusable solution (e.g. "Can i call by
  // 10am") and Approve must stay disabled with the reason surfaced. Backend re-checks authoritatively.
  const qualityClass = resolutionQualityClass(meaningfulSteps.join('\n'));

  // Mirrors the backend approval gate exactly (backend remains authoritative).
  const validation = [];
  if (!meaningfulSteps.length) validation.push('At least one meaningful resolution step');
  else if (qualityClass) validation.push('A reusable corrective resolution (not a callback, handoff, escalation or status note)');
  if (!category.trim()) validation.push('An approved category');
  // An outcome states what the CUSTOMER ends up with. These words state what a reviewer did — the
  // bookkeeping of the review, not its result. Two clinic approvals went through as "Sanitized" and
  // then "Updated": both satisfied "non-empty" and told the next reader nothing. Mirrors the backend
  // gate exactly (backend remains authoritative); only a whole-value match is rejected, so a short
  // real outcome like "Appointment booked" passes.
  if (!outcome.trim()) validation.push('A final outcome');
  else if (BOOKKEEPING_OUTCOMES.has(outcome.trim().toLowerCase().replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim()))
    validation.push('A final outcome that describes the customer result, not the review action');
  if (!title.trim()) validation.push('A problem title');
  if (!source.trim()) validation.push('A source classification');
  if (!duplicateDecision.trim()) validation.push('A duplicate decision');
  if (!sanitizedConfirmed) validation.push('Sanitization confirmation');
  const canApprove = validation.length === 0 && !busy;

  // Submission bar — mirrors the backend _validate_for_submission exactly (backend re-validates on
  // /submit-for-review). It is the AUTHOR vouching the draft is complete + reusable, so it requires the
  // reusable-resolution + canonical-category + real-outcome + title gate, but NOT the reviewer-only
  // obligations (sanitization confirmation + duplicate decision), which the independent reviewer owns.
  const submitValidation = [];
  if (!meaningfulSteps.length) submitValidation.push('At least one meaningful resolution step');
  else if (qualityClass) submitValidation.push('A reusable corrective resolution (not a callback, handoff, escalation or status note)');
  if (!category.trim()) submitValidation.push('A tenant category');
  if (!outcome.trim()) submitValidation.push('A final outcome');
  else if (BOOKKEEPING_OUTCOMES.has(outcome.trim().toLowerCase().replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim()))
    submitValidation.push('A final outcome that describes the customer result, not the review action');
  if (!title.trim()) submitValidation.push('A problem title');
  const canSubmit = submitValidation.length === 0 && !busy;

  const buildPayload = () => ({
    title: title.trim(),
    problem_summary: problemSummary.trim(),
    description: problemSummary.trim(),
    resolution_steps: meaningfulSteps,
    resolution: meaningfulSteps.join('\n'),
    outcome: outcome.trim(),
    category: category.trim(),
    tags: tagsText.split(',').map((s) => s.trim()).filter(Boolean),
    source: source.trim(),
    sanitized: true,
    duplicate_decision: duplicateDecision,
    duplicate_of: duplicateDecision === 'new' ? undefined : (duplicateOf || undefined),
  });

  // Author/draft payload — the content only. It deliberately omits `source` (provenance is preserved
  // server-side and must never be falsified), `sanitized` and the duplicate decision (reviewer-owned).
  const buildDraftPayload = () => ({
    title: title.trim(),
    problem_summary: problemSummary.trim(),
    description: problemSummary.trim(),
    resolution_steps: meaningfulSteps,
    resolution: meaningfulSteps.join('\n'),
    outcome: outcome.trim(),
    category: category.trim(),
    tags: tagsText.split(',').map((s) => s.trim()).filter(Boolean),
  });

  const post = async (path, body, okTitle, okDesc, failTitle) => {
    setBusy(true);
    try {
      const r = await makeAuthenticatedRequest(`/api/v1/solutions/${solution.id}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const detail = err && err.detail;
        const msg = Array.isArray(detail?.validation_errors)
          ? detail.validation_errors.join(' ')
          : (typeof detail === 'string' ? detail : (detail?.message || `HTTP ${r.status}`));
        throw new Error(msg);
      }
      toast && toast({ title: okTitle, description: okDesc });
      onDone && onDone();
    } catch (e) {
      toast && toast({ title: failTitle, description: e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const doApprove = () => { if (canApprove) post('approve', buildPayload(), 'Solution approved', 'It is now available to AI Chat and Voice.', 'Approval failed'); };
  const doReturn = () => post('return-for-correction', { reason: reason.trim() }, 'Returned for correction', 'The submitter can add more detail and resubmit.', 'Could not return');
  const doReject = () => post('reject', { review_note: reason.trim() }, 'Solution rejected', 'It will not be used by AI.', 'Rejection failed');

  // Durable draft save — persists the authored steps + outcome + canonical category WITHOUT changing the
  // lifecycle (the record stays "suggested": internal-only, non-retrievable, not approved). Allowed even when
  // incomplete, so a reviewer-author can save work in progress. PUT /solutions/{id} is the canonical editor.
  const saveDraft = async () => {
    setBusy(true);
    try {
      const r = await makeAuthenticatedRequest(`/api/v1/solutions/${solution.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildDraftPayload()),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const detail = err && err.detail;
        throw new Error(typeof detail === 'string' ? detail : (detail?.message || `HTTP ${r.status}`));
      }
      toast && toast({ title: 'Draft saved', description: 'Your edits are saved. The draft stays internal until it is submitted and a different manager approves it.' });
      onDone && onDone();
    } catch (e) {
      toast && toast({ title: 'Could not save draft', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  // Submit the completed draft for independent review: suggested -> pending_approval (NEVER directly approved).
  const doSubmit = () => {
    if (canSubmit) post('submit-for-review', buildDraftPayload(),
      'Submitted for review', 'A different manager or admin can now independently review, sanitize and approve it.',
      'Could not submit for review');
  };

  const label = 'block text-sm font-semibold text-gray-700 mb-1';
  const input = 'w-full border rounded px-3 py-2 text-sm';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Review solution">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-yellow-600" /> {isAuthoring ? 'Edit & Submit Draft' : 'Review Solution'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Submitted by {solution.submitted_by_name || tr('sb.review.staff')}
              {solution.source_conversation_id ? ` · Conversation ${shortRef(solution.source_conversation_id)}` : ''}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div>
          <label className={label}>Problem title</label>
          <input className={input} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>

        <div>
          <label className={label}>Reusable problem summary</label>
          <textarea className={`${input} min-h-[64px]`} value={problemSummary} onChange={(e) => setProblemSummary(e.target.value)} />
        </div>

        <div>
          <label className={label}>Ordered resolution steps</label>
          <div className="space-y-2">
            {steps.map((s, i) => (
              <div key={i} className="flex gap-2 items-center">
                <span className="text-xs text-gray-400 w-5 text-right">{i + 1}.</span>
                <input className={input} value={s} placeholder={`Step ${i + 1}`}
                  onChange={(e) => setSteps(steps.map((v, j) => (j === i ? e.target.value : v)))} />
                <button aria-label="Remove step" className="text-red-500 hover:bg-red-50 p-1 rounded"
                  onClick={() => setSteps(steps.length > 1 ? steps.filter((_, j) => j !== i) : [''])}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => setSteps([...steps, ''])}>
            <Plus className="w-4 h-4 mr-1" /> Add step
          </Button>
        </div>

        {/* Resolution-quality result — an invalid candidate (callback/handoff/escalation/status-only, e.g.
            "Can i call by 10am") is clearly flagged non-approvable; Return-for-correction / Reject remain. */}
        {qualityClass && (
          <div className="rounded border border-red-300 bg-red-50 p-3 flex items-start gap-2 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">This candidate cannot be approved as a reusable solution.</p>
              <p>{RES_QUALITY_MSG}</p>
              <p className="mt-1 text-xs text-red-600">Use “Return for Correction” to ask the submitter for the actual fix, or Reject it.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Final outcome</label>
            <input className={input} value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="What the customer ends up with — e.g. Appointment booked and confirmed" />
          </div>
          <div>
            <label className={label}>Category</label>
            {/* Governed SELECT of the tenant's canonical categories only — NO free text. */}
            {categoriesLoaded && categoryOptions.length === 0 ? (
              <p className="text-xs text-amber-700 border rounded px-3 py-2 bg-amber-50">
                No categories are configured for this business. Add one in Category Management before approving.
              </p>
            ) : (
              <select
                className={input}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                aria-label="Category"
                disabled={!categoriesLoaded}
              >
                <option value="">{categoriesLoaded ? 'Select a category…' : 'Loading categories…'}</option>
                {categoryOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Tags (comma separated)</label>
            <input className={input} value={tagsText} onChange={(e) => setTagsText(e.target.value)} aria-label="Tags (comma separated)" />
          </div>
          <div>
            <label className={label}>Source classification</label>
            {source ? (
              // Known provenance — READ-ONLY. Never falsify a record's true origin to satisfy a gate.
              <input className={`${input} bg-gray-100 text-gray-600`} readOnly aria-readonly="true"
                     value={SOURCE_LABELS[source] || source} aria-label="Source classification" />
            ) : (
              // Legacy candidate with no stored provenance — the reviewer must classify it once.
              <select className={input} value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source classification">
                <option value="">Select a source…</option>
                <option value="human_resolution">Human resolution</option>
                <option value="owner_supplied">Owner supplied</option>
                <option value="imported">Imported</option>
              </select>
            )}
          </div>
        </div>

        {/* Source channel provenance — set at the submission boundary (server-canonical), read-only here. */}
        <div>
          <label className={label}>Source channel (provenance)</label>
          {/* Friendly label, never the raw enum. This field showed `human_chat` verbatim, and the
              provenance vocabulary now also carries `solution_bank` — internal values that mean
              nothing to a reviewer. Unrecognised/legacy values fall back to the product's existing
              "unknown" wording rather than leaking whatever string is stored. */}
          <input className={`${input} bg-gray-100 text-gray-600`}
                 value={CHANNEL_LABELS[solution.source_channel] ? tr(CHANNEL_LABELS[solution.source_channel])
                                                                : tr('sb.review.ch.unknown')}
                 readOnly aria-readonly="true" />
        </div>

        {/* Duplicate decision + sanitization are REVIEWER-only — they are the independent reviewer's
            responsibility after a draft is submitted, not part of authoring a suggested draft. */}
        {isReviewing && (<>
        {/* Duplicate decision */}
        <div>
          <label className={label}>Duplicate decision</label>
          <div className="flex gap-2 flex-wrap">
            {DUP_DECISIONS.map((d) => (
              <button key={d} onClick={() => setDuplicateDecision(d)}
                className={`px-3 py-1 rounded text-sm border ${duplicateDecision === d ? 'bg-yellow-600 text-white border-yellow-600' : 'bg-white text-gray-700'}`}>
                {d}
              </button>
            ))}
          </div>
          {duplicateDecision !== 'new' && (
            <select className={`${input} mt-2`} value={duplicateOf} onChange={(e) => setDuplicateOf(e.target.value)}>
              <option value="">Select the related approved solution…</option>
              {approvedList.map((s) => (<option key={s.id} value={s.id}>{s.title}</option>))}
            </select>
          )}
        </div>

        {/* Sanitization */}
        <div className="rounded border p-3 bg-gray-50">
          {sanitizeFindings.length > 0 ? (
            <div className="flex items-start gap-2 text-sm text-amber-700 mb-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <span>Possible sensitive content detected: {sanitizeFindings.join(', ')}. Remove it from the fields above before approving.</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-green-700 mb-2">
              <ShieldCheck className="w-4 h-4" /> No obvious PII, secrets or raw identifiers detected.
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={sanitizedConfirmed} onChange={(e) => setSanitizedConfirmed(e.target.checked)} />
            I confirm this solution contains no customer PII, credentials, raw identifiers or private notes.
          </label>
        </div>
        </>)}

        {/* Review history */}
        {Array.isArray(solution.review_history) && solution.review_history.length > 0 && (
          <div>
            <label className={label}>Review history</label>
            <ul className="text-xs text-gray-500 space-y-1">
              {solution.review_history.map((h, i) => (
                <li key={i}>• {h.action}{h.at ? ` · ${String(h.at).slice(0, 10)}` : ''}{h.note ? ` — ${h.note}` : ''}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Reason (used by return / reject) */}
        <div>
          <label className={label}>Note to submitter (for return or reject)</label>
          <input className={input} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional note" />
        </div>

        {isReviewing && !canApprove && validation.length > 0 && (
          <p className="text-xs text-amber-700">Approve is disabled until: {validation.join(', ')}.</p>
        )}
        {isAuthoring && !canSubmit && submitValidation.length > 0 && (
          <p className="text-xs text-amber-700">Submit for Review is disabled until: {submitValidation.join(', ')}.</p>
        )}

        <div className="flex gap-2 pt-3 border-t sticky bottom-0 bg-white">
          {isAuthoring ? (
            <>
              <Button onClick={saveDraft} disabled={busy} variant="outline" className="flex-1">
                Save draft
              </Button>
              <Button onClick={doSubmit} disabled={!canSubmit} className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50">
                <CheckCircle className="w-4 h-4 mr-2" /> Submit for Review
              </Button>
            </>
          ) : isReviewing ? (
            <>
              <Button onClick={doApprove} disabled={!canApprove} className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50">
                <CheckCircle className="w-4 h-4 mr-2" /> Approve
              </Button>
              <Button onClick={doReturn} disabled={busy} variant="outline" className="flex-1 border-amber-500 text-amber-700 hover:bg-amber-50">
                <RotateCcw className="w-4 h-4 mr-2" /> Return for Correction
              </Button>
              <Button onClick={doReject} disabled={busy} variant="outline" className="flex-1 border-red-500 text-red-600 hover:bg-red-50">
                <X className="w-4 h-4 mr-2" /> Reject
              </Button>
            </>
          ) : (
            <p className="text-xs text-gray-500 flex-1 self-center">
              This solution is {curStatus || 'in a non-editable state'} — it has no review actions here.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
